import { Divider } from "antd";
import { PureComponent } from "react";

import { BTText } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTableCustom } from "commonComponents/btWrappers/BTTableCustom/BTTableCustom";
import { type LineItemPriceBreakdownInfo } from "commonComponents/entity/lineItem/LineItemPriceBreakdown/LineItemPriceBreakdownBase.types";
import { CurrencyDisplay } from "commonComponents/financial/CurrencyDisplay/CurrencyDisplay";
import { PercentageDisplay } from "commonComponents/financial/PercentageDisplay/PercentageDisplay";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";

import "./LineItemPriceBreakdownBase.less";

import { TaxRateBreakdown } from "../../../../entity/tax/common/TaxRateBreakdown/TaxRateBreakdown";

interface ILineItemPriceRowProps {
    rowClassName?: string;
    title: string;
    value: number;
    valueTestId?: string;
    valueClassName?: string;
}

class LineItemPriceRow extends PureComponent<ILineItemPriceRowProps> {
    render() {
        const { rowClassName, title, value, valueTestId, valueClassName } = this.props;
        return (
            <BTTableCustom.BodyRow className={cn("FullBreakdownRow", rowClassName)}>
                <BTTableCustom.BodyCell
                    className="TitleColumn"
                    data-testid={`${valueTestId}-label`}
                >
                    {title}
                </BTTableCustom.BodyCell>
                <BTTableCustom.BodyCell className={cn("FinancialColumn", valueClassName)}>
                    <CurrencyDisplay value={value} data-testid={valueTestId} />
                </BTTableCustom.BodyCell>
            </BTTableCustom.BodyRow>
        );
    }
}

export interface ITotalPriceDisplayProps {
    subtotal?: number;
    taxAmount?: number;
    priceLabel: string;
    totalPrice: number;
    useFormFieldLabel: boolean;
    removeBottomMargin?: boolean;
    leftAlignContent: boolean;
}

export const TotalPriceDisplay: React.FunctionComponent<ITotalPriceDisplayProps> = (props) => {
    const {
        priceLabel,
        subtotal,
        taxAmount,
        totalPrice,
        useFormFieldLabel,
        removeBottomMargin,
        leftAlignContent,
    } = props;

    const showSubtotal = subtotal !== undefined && taxAmount !== undefined;

    if (useFormFieldLabel) {
        return (
            <BTFormItem
                label={priceLabel}
                className={cn({ "margin-bottom-zero": removeBottomMargin })}
            >
                <CurrencyDisplay data-testid="priceDisplay" value={totalPrice} />
            </BTFormItem>
        );
    } else {
        return (
            <div
                className={cn("flex flex-row", "TaxPriceTotalLabelRow", "grow", {
                    "justify-end": !leftAlignContent,
                })}
            >
                <div className="flex flex-col">
                    {showSubtotal && (
                        <>
                            <div className="flex flex-row justify-between gap-20">
                                <div className="flex flex-col">
                                    <BTText size="distinct-sm" text="Subtotal" />
                                </div>
                                <div className="flex flex-col">
                                    <BTText
                                        size="normal-md"
                                        text={
                                            <CurrencyDisplay
                                                data-testid="subtotalDisplay"
                                                value={subtotal}
                                            />
                                        }
                                    />
                                </div>
                            </div>

                            <div className="flex flex-row justify-between gap-20">
                                <div className="flex flex-col">
                                    <BTText size="distinct-sm" text="Tax" />
                                </div>
                                <div className="flex flex-col">
                                    <BTText
                                        size="normal-md"
                                        text={
                                            <CurrencyDisplay
                                                data-testid="totalTaxDisplay"
                                                value={taxAmount}
                                            />
                                        }
                                    />
                                </div>
                            </div>
                            <Divider style={{ marginTop: 10, marginBottom: 10 }} />
                        </>
                    )}
                    <div className="flex flex-row justify-between gap-16">
                        <div className="flex flex-col">
                            <BTText size="distinct-md" text={priceLabel} />
                        </div>
                        <div className="flex flex-col">
                            <BTText
                                size="distinct-md"
                                text={
                                    <CurrencyDisplay
                                        data-testid="priceDisplay"
                                        value={totalPrice}
                                    />
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
};

class DividerRow extends PureComponent {
    render() {
        return (
            <BTTableCustom.BodyRow className="FullBreakdownRow DividerRow">
                <BTTableCustom.BodyCell className="DividerColumn" colSpan={2}>
                    <Divider className="BreakdownDivider" />
                </BTTableCustom.BodyCell>
            </BTTableCustom.BodyRow>
        );
    }
}

interface ITaxBreakdownPopoverProps {
    priceBreakdownInfo: LineItemPriceBreakdownInfo;
    isInGrid: boolean;
    useFormFieldLabel: boolean;
    leftAlignContent: boolean;
    showBuilderCost: boolean;
    showMarkup: boolean;
    showMargin: boolean;
    rightAlignPopover: boolean;
    costLabelText: string;
    totalPriceLabelText: string;
    linkPriceText: string;
}

const TaxBreakdownPopover: React.FunctionComponent<ITaxBreakdownPopoverProps> = (props) => {
    const {
        priceBreakdownInfo,
        isInGrid,
        leftAlignContent,
        showBuilderCost,
        showMargin,
        showMarkup,
        rightAlignPopover,
        costLabelText,
        totalPriceLabelText,
        linkPriceText,
    } = props;

    const component = (
        <BTPopover
            removeSpacing
            widthType="unrestricted"
            placement={rightAlignPopover ? "right" : "left"}
            content={
                <BTTableCustom.Table>
                    <ShowOnPortal
                        builder={showBuilderCost}
                        render={() => (
                            <LineItemPriceRow
                                title={costLabelText}
                                value={priceBreakdownInfo.builderCost!}
                                valueTestId="builderCost"
                            />
                        )}
                    />
                    <ShowOnPortal
                        builder={showMarkup}
                        render={() => (
                            <LineItemPriceRow
                                title="Markup"
                                value={priceBreakdownInfo.totalMarkup!}
                                valueTestId="markup"
                            />
                        )}
                    />
                    <LineItemPriceRow
                        rowClassName="text-bold TotalBorder"
                        title="Subtotal"
                        value={priceBreakdownInfo.subtotal!}
                        valueTestId="subtotal"
                        valueClassName="TotalBorder"
                    />
                    <DividerRow />
                    <TaxRateBreakdown
                        taxRateBreakdownInfo={priceBreakdownInfo.taxRateBreakdownInfo!}
                    />
                    <DividerRow />
                    <LineItemPriceRow
                        rowClassName="text-bold"
                        title={totalPriceLabelText}
                        value={priceBreakdownInfo.totalPrice}
                        valueTestId="totalPriceTaxes"
                    />
                    <ShowOnPortal
                        builder={showMargin}
                        render={() => (
                            <BTTableCustom.BodyRow className="FullBreakdownRow">
                                <BTTableCustom.BodyCell className="TitleColumn">
                                    Total margin %
                                </BTTableCustom.BodyCell>
                                <BTTableCustom.BodyCell className="FinancialColumn">
                                    <PercentageDisplay
                                        data-testid="marginPercentTaxes"
                                        value={priceBreakdownInfo.totalMargin}
                                    />
                                </BTTableCustom.BodyCell>
                            </BTTableCustom.BodyRow>
                        )}
                    />
                </BTTableCustom.Table>
            }
            trigger="click"
            overlayClassName="text-right LineItemPriceBreakdown"
        >
            <BTButtonDeprecated
                data-testid="viewFullBreakdown"
                className="viewFullBreakdown"
                type="link"
                isolated
            >
                See full {linkPriceText} breakdown
            </BTButtonDeprecated>
        </BTPopover>
    );
    if (isInGrid) {
        return <BTRow justify={leftAlignContent ? "start" : "end"}>{component}</BTRow>;
    } else {
        return component;
    }
};

interface ILineItemPriceBreakdownBaseProps {
    costLabelText: string;
    isFlatFee: boolean;
    leftAlignContent: boolean;
    lineItemPriceBreakdownInfo: LineItemPriceBreakdownInfo;
    linkPriceText: string;
    maxTransactionAmount?: number;
    paymentLimitAmount: number | null;
    renderCustomPriceDisplay?: (
        props: Omit<ITotalPriceDisplayProps, "removeBottomMargin">,
    ) => React.ReactNode;
    rightAlignPopover: boolean;
    showBreakdown: boolean;
    showBreakdownButton?: boolean;
    showBuilderCost: boolean;
    showMargin: boolean;
    showMarkup: boolean;
    showPriceOnly: boolean;
    totalPriceLabelText: string;
    useFormFieldLabel?: boolean;
    useUpdatedComponentWithTaxRates: boolean;
}

export class LineItemPriceBreakdownBase extends PureComponent<ILineItemPriceBreakdownBaseProps> {
    render() {
        const {
            useFormFieldLabel,
            useUpdatedComponentWithTaxRates,
            lineItemPriceBreakdownInfo,
            leftAlignContent,
            totalPriceLabelText,
            showPriceOnly,
            isFlatFee,
        } = this.props;

        if (useUpdatedComponentWithTaxRates) {
            const priceDisplay = this.props.renderCustomPriceDisplay ? (
                this.props.renderCustomPriceDisplay({
                    priceLabel: totalPriceLabelText,
                    totalPrice: lineItemPriceBreakdownInfo.totalPrice,
                    useFormFieldLabel: useFormFieldLabel!,
                    leftAlignContent: leftAlignContent,
                })
            ) : (
                <TotalPriceDisplay
                    priceLabel={totalPriceLabelText}
                    totalPrice={lineItemPriceBreakdownInfo.subtotal!}
                    useFormFieldLabel={useFormFieldLabel!}
                    leftAlignContent={leftAlignContent}
                />
            );
            if (showPriceOnly) {
                return (
                    <>
                        <BTRow justify={isFlatFee ? "start" : "end"}>
                            {this.getLimitWarning()}
                        </BTRow>
                        <div data-testid="priceBreakdown">{priceDisplay}</div>
                    </>
                );
            } else {
                return this.getPriceBreakdownWithTaxes();
            }
        } else {
            return this.getPriceBreakdownWithoutTaxes();
        }
    }

    private getPriceBreakdownWithTaxes = () => {
        const {
            lineItemPriceBreakdownInfo,
            useFormFieldLabel,
            renderCustomPriceDisplay,
            leftAlignContent,
            showMarkup,
            showMargin,
            showBuilderCost,
            totalPriceLabelText,
            costLabelText,
            linkPriceText,
            rightAlignPopover,
            showBreakdown,
            isFlatFee,
        } = this.props;
        const priceDisplayProps: ITotalPriceDisplayProps = {
            priceLabel: totalPriceLabelText,
            totalPrice: lineItemPriceBreakdownInfo.totalPrice,
            useFormFieldLabel: useFormFieldLabel!,
            removeBottomMargin: true,
            leftAlignContent: leftAlignContent,
        };
        const breakdown = showBreakdown && (
            <TaxBreakdownPopover
                priceBreakdownInfo={lineItemPriceBreakdownInfo}
                isInGrid={!useFormFieldLabel}
                useFormFieldLabel={useFormFieldLabel!}
                leftAlignContent={leftAlignContent}
                showBuilderCost={showBuilderCost}
                showMargin={showMargin}
                showMarkup={showMarkup}
                totalPriceLabelText={totalPriceLabelText}
                costLabelText={costLabelText}
                linkPriceText={linkPriceText}
                rightAlignPopover={rightAlignPopover}
            />
        );
        const priceDisplay: React.ReactNode = renderCustomPriceDisplay ? (
            <div data-testid="priceBreakdown">
                <BTRow justify={isFlatFee ? "start" : "end"}>{this.getLimitWarning()}</BTRow>
                {renderCustomPriceDisplay(priceDisplayProps)}
                {breakdown}
            </div>
        ) : (
            <div data-testid="priceBreakdown">
                <BTRow justify={isFlatFee ? "start" : "end"}>{this.getLimitWarning()}</BTRow>
                <TotalPriceDisplay
                    priceLabel={totalPriceLabelText}
                    subtotal={lineItemPriceBreakdownInfo.subtotal}
                    taxAmount={lineItemPriceBreakdownInfo.taxRateBreakdownInfo?.totalTax}
                    totalPrice={lineItemPriceBreakdownInfo.totalPrice}
                    useFormFieldLabel={useFormFieldLabel!}
                    removeBottomMargin
                    leftAlignContent={leftAlignContent}
                />
                {breakdown}
            </div>
        );

        return priceDisplay;
    };

    private readonly getPriceBreakdownWithoutTaxes = () => {
        const {
            lineItemPriceBreakdownInfo,
            showBreakdownButton = true,
            totalPriceLabelText,
        } = this.props;

        return (
            <div data-testid="priceBreakdown">
                <BTRow className="priceTotalLabelRow" justify="end">
                    {totalPriceLabelText}:&nbsp;
                    <CurrencyDisplay value={lineItemPriceBreakdownInfo.ownerPrice!} />
                </BTRow>
                {showBreakdownButton && (
                    <BTRow justify="end">
                        <BTPopover
                            removeSpacing
                            widthType="unrestricted"
                            placement="left"
                            content={
                                <BTTableCustom.Table>
                                    <BTTableCustom.BodyRow className="full-breakdown-row">
                                        <BTTableCustom.BodyCell className="full-breakdown-cell">
                                            Total builder cost
                                        </BTTableCustom.BodyCell>
                                        <BTTableCustom.BodyCell className="full-breakdown-cell">
                                            <CurrencyDisplay
                                                value={lineItemPriceBreakdownInfo.builderCost!}
                                                data-testid="totalBuilderCost"
                                            />
                                        </BTTableCustom.BodyCell>
                                    </BTTableCustom.BodyRow>
                                    <BTTableCustom.BodyRow className="full-breakdown-row">
                                        <BTTableCustom.BodyCell className="full-breakdown-cell">
                                            Total markup
                                        </BTTableCustom.BodyCell>
                                        <BTTableCustom.BodyCell className="full-breakdown-cell">
                                            <CurrencyDisplay
                                                value={lineItemPriceBreakdownInfo.totalMarkup!}
                                                data-testid="totalMarkup"
                                            />
                                        </BTTableCustom.BodyCell>
                                    </BTTableCustom.BodyRow>
                                    <BTTableCustom.BodyRow className="full-breakdown-row owner-price-row">
                                        <BTTableCustom.BodyCell className="full-breakdown-cell">
                                            {totalPriceLabelText}
                                        </BTTableCustom.BodyCell>
                                        <BTTableCustom.BodyCell className="full-breakdown-cell">
                                            <CurrencyDisplay
                                                value={lineItemPriceBreakdownInfo.ownerPrice!}
                                                data-testid="totalPrice"
                                            />
                                        </BTTableCustom.BodyCell>
                                    </BTTableCustom.BodyRow>
                                    <BTTableCustom.BodyRow className="full-breakdown-row">
                                        <BTTableCustom.BodyCell className="full-breakdown-cell">
                                            Total margin %
                                        </BTTableCustom.BodyCell>
                                        <BTTableCustom.BodyCell className="full-breakdown-cell">
                                            <PercentageDisplay
                                                data-testid="marginPercent"
                                                value={lineItemPriceBreakdownInfo.totalMargin}
                                            />
                                        </BTTableCustom.BodyCell>
                                    </BTTableCustom.BodyRow>
                                </BTTableCustom.Table>
                            }
                            trigger="click"
                            overlayClassName="text-right FullBreakdown"
                        >
                            <BTButtonDeprecated
                                data-testid="viewFullBreakdown"
                                className="viewFullBreakdown"
                                type="link"
                                isolated
                            >
                                See full price breakdown
                            </BTButtonDeprecated>
                        </BTPopover>
                    </BTRow>
                )}
            </div>
        );
    };

    private getLimitWarning = () => {
        const { paymentLimitAmount, lineItemPriceBreakdownInfo, isFlatFee } = this.props;

        if (
            paymentLimitAmount == null ||
            lineItemPriceBreakdownInfo.totalPrice <= paymentLimitAmount
        ) {
            return null;
        }

        return (
            <div className={isFlatFee ? "" : "padding-top-sm"}>
                <span
                    className={cn("PaymentLimitText", {
                        "padding-top-xs": !isFlatFee,
                    })}
                    data-testid="paymentLimitWarning"
                >
                    Your client cannot send you an online payment over{" "}
                    <CurrencyDisplay value={paymentLimitAmount} />{" "}
                    <BTExternalLink
                        href="https://helpcenter.buildertrend.net/s/article/Buildertrend-Payments-Faqs"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Learn more
                    </BTExternalLink>
                </span>
            </div>
        );
    };
}
